import {parseNumber} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import {assert} from 'Asserts';
import {AaNaturalAnalog} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {AA_MONOMER_LIBRARY_URL, getDefaultAaMonomerColor} from 'molbio/aa-monomers/AaMonomerConstants';
import {
  APIAaMonomer,
  AaMonomerValidationInput,
  createAaMonomers,
  validateAaMonomerInput,
} from 'molbio/aa-monomers/AaMonomersDAO';
import {useCanCreateAaMonomersAndRedirect} from 'molbio/aa-monomers/admin/useCanCreateAaMonomers';
import MonomerManualCreationBuildPane from 'molbio/monomers/admin/MonomerManualCreationBuildPane';
import MonomerStructureAndWeightConfigurationPane from 'molbio/monomers/admin/MonomerStructureAndWeightConfigurationPane';
import SharedMonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from 'molbio/monomers/admin/SharedMonomerConfigurationPageContainer';
import {
  MonomerManualCreationStepKey,
  useMonomerManualCreationState,
} from 'molbio/monomers/admin/useMonomerManualCreationState';
import {SharedMonomerValidationOutput} from 'molbio/monomers/SharedMonomerConstants';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import StepNav, {StepItemStatus} from 'ui-library/StepNav';

import AaMonomerManualCreationDefinePane from './AaMonomerManualCreationDefinePane';

const DEFAULT_NATURAL_ANALOG = AaNaturalAnalog.A;

export default function AaMonomerManualCreationPage(): JSX.Element {
  const {isLoading} = useCanCreateAaMonomersAndRedirect();

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }

  return <AaMonomerManualCreationWizard />;
}

function AaMonomerManualCreationWizard(): JSX.Element {
  const {classes} = useStyles();
  const [currentStepKey, setCurrentStepKey] = useState(MonomerManualCreationStepKey.BUILD);
  const [naturalAnalogue, setNaturalAnalogue] = useState<AaNaturalAnalog>(DEFAULT_NATURAL_ANALOG);
  const [color, setColor] = useState<string>(getDefaultAaMonomerColor(DEFAULT_NATURAL_ANALOG));

  const runValidation = useCallback(
    async (monomerValidationInput: AaMonomerValidationInput): Promise<SharedMonomerValidationOutput> => {
      const outputs = await validateAaMonomerInput([monomerValidationInput]);
      assert(outputs.length === 1, 'Expected exactly one monomer validation output');
      return outputs[0];
    },
    []
  );

  const creationState = useMonomerManualCreationState<AaMonomerValidationInput>({
    runValidation,
    getTypeSpecificFields: () => ({naturalAnalogue, color}),
  });

  const onChangeNaturalAnalogue = (newNaturalAnalogue: AaNaturalAnalog): void => {
    const newColor = getDefaultAaMonomerColor(newNaturalAnalogue);
    setNaturalAnalogue(newNaturalAnalogue);
    setColor(newColor);
    creationState.validate({naturalAnalogue: newNaturalAnalogue, color: newColor});
  };

  const statusByStep = {
    [MonomerManualCreationStepKey.BUILD]: creationState.buildPhaseCompleted
      ? StepItemStatus.DONE
      : StepItemStatus.UNVISITED,
    [MonomerManualCreationStepKey.DEFINE]: creationState.definePhaseCompleted
      ? StepItemStatus.DONE
      : StepItemStatus.UNVISITED,
  };

  const createMonomer = async (): Promise<void> => {
    assert(
      [creationState.name, creationState.symbol, naturalAnalogue].every((field) => field != null),
      'Unexpected null input fields received for monomer creation.'
    );
    const input: APIAaMonomer = {
      name: creationState.name,
      symbol: creationState.symbol,
      naturalAnalog: naturalAnalogue,
      smiles: creationState.smilesInput,
      color,
      customMolecularWeight:
        creationState.customMolecularWeight != null ? parseNumber(creationState.customMolecularWeight) : null,
    };

    try {
      LoadingScreen.show('Creating...');
      await createAaMonomers([input]);
      notifications.success(`Created AA monomer ${creationState.symbol}`);
      navigateToURL(AA_MONOMER_LIBRARY_URL);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    } finally {
      LoadingScreen.hide();
    }
  };

  const buildPaneButtons = (
    <div>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        text="Next: Define"
        size={ButtonSize.MEDIUM}
        disabled={
          !creationState.buildPhaseCompleted ||
          creationState.editingMolecularWeight ||
          creationState.hasCustomWeightError
        }
        onClick={() => {
          setCurrentStepKey(MonomerManualCreationStepKey.DEFINE);
        }}
        dataTestAttrs={{
          component: 'AaMonomerManualCreationPage',
          element: 'nextButton',
        }}
      />
    </div>
  );

  const definePaneButtons = (
    <div className={classes.definePaneButtons}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        text="Back"
        size={ButtonSize.MEDIUM}
        onClick={() => {
          setCurrentStepKey(MonomerManualCreationStepKey.BUILD);
        }}
      />
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        text="Add new monomer"
        size={ButtonSize.MEDIUM}
        disabled={
          !creationState.definePhaseCompleted ||
          creationState.editingMolecularWeight ||
          creationState.hasCustomWeightError
        }
        onClick={createMonomer}
        dataTestAttrs={{
          component: 'AaMonomerManualCreationPage',
          element: 'addButton',
        }}
      />
    </div>
  );

  const rightButtonGroup =
    currentStepKey === MonomerManualCreationStepKey.BUILD ? buildPaneButtons : definePaneButtons;

  const navSteps = [
    {
      key: MonomerManualCreationStepKey.BUILD,
      title: 'Build',
    },
    {
      key: MonomerManualCreationStepKey.DEFINE,
      title: 'Define',
    },
  ];

  return (
    <div
      className={classes.pageContainer}
      {...getTestProps({component: 'AaMonomerManualCreationPage', element: 'root'})}
    >
      <SharedMonomerConfigurationPageContainer
        purpose={MONOMER_CONFIGURATION_PURPOSE.CREATE}
        libraryName="AA monomer library"
        libraryUrl={AA_MONOMER_LIBRARY_URL}
        methodTitle="Create new AA monomer"
        changeMethodUrl="/aa-monomers/create"
      />
      <div className={classes.stepNav}>
        <StepNav<MonomerManualCreationStepKey>
          steps={navSteps}
          statusByStep={statusByStep}
          currentStepKey={currentStepKey}
          onUpdateCurrentStep={() => {}}
        />
      </div>
      <div className={classes.middleSectionContainer}>
        <div className={classes.monomerPropertiesPane}>
          {currentStepKey === MonomerManualCreationStepKey.BUILD ? (
            <MonomerManualCreationBuildPane
              smilesInput={creationState.smilesInput}
              onChangeSmilesInput={creationState.onChangeSmilesInput}
              canonicalSmiles={creationState.canonicalSmiles}
              attachmentPointsInput={creationState.attachmentPointsInput}
              onChangeAttachmentPointsInput={creationState.onChangeAttachmentPointsInput}
              calculatedAttachmentPoints={creationState.calculatedAttachmentPoints}
              validationErrors={creationState.validationErrors}
            />
          ) : (
            <AaMonomerManualCreationDefinePane
              name={creationState.name}
              onChangeName={creationState.onChangeName}
              symbol={creationState.symbol}
              onChangeSymbol={creationState.onChangeSymbol}
              color={color}
              onChangeColor={setColor}
              naturalAnalogue={naturalAnalogue}
              onChangeNaturalAnalogue={onChangeNaturalAnalogue}
              validationErrors={creationState.validationErrors}
            />
          )}
        </div>
        <MonomerStructureAndWeightConfigurationPane
          smilesInput={creationState.smilesInput}
          customMolecularWeight={creationState.customMolecularWeight}
          calculatedMolecularWeight={creationState.calculatedMolecularWeight}
          onChangeCustomMolecularWeight={creationState.onChangeCustomMolecularWeight}
          validationErrors={creationState.validationErrors}
          editingMolecularWeight={creationState.editingMolecularWeight}
          onChangeEditingMolecularWeight={creationState.setEditingMolecularWeight}
          structureImage={creationState.structureImage}
          hasCustomWeightError={creationState.hasCustomWeightError}
          onChangeHasCustomWeightError={creationState.setHasCustomWeightError}
        />
      </div>
      <div className={classes.submitBar}>
        <div className={classes.buttonsContainer}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={() => {
              navigateToURL(AA_MONOMER_LIBRARY_URL);
            }}
            size={ButtonSize.MEDIUM}
            text="Cancel"
          />
          {rightButtonGroup}
        </div>
      </div>
    </div>
  );
}

const useStyles = createStyles({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  stepNav: {
    paddingLeft: 40,
    paddingBottom: 20,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flex: '0 0 auto',
  },
  middleSectionContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  monomerPropertiesPane: {
    width: '50%',
    borderRight: `1px solid ${GRAY_LIGHT}`,
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    padding: 16,
    gap: 10,
    boxShadow: '0px -2px 4px rgba(92, 124, 131, 0.1)',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  definePaneButtons: {
    display: 'flex',
    gap: 12,
  },
});
